import { useQuery } from '@tanstack/react-query';
import { DashboardResponse, UNIT_FLOW_ATTENTION_THRESHOLD_CENTS } from '@sourcito/schemas';
import { apiFetch } from './api';
import { claimDeadlineState } from './claims';
import { formatFeeCenterCurrency } from './fee-center';

/** Today home page data — one call, sections null when a module is off. */
export function useDashboard(enabled = true) {
  return useQuery({
    queryKey: ['dashboard'] as const,
    enabled,
    queryFn: () => apiFetch('/api/dashboard', { responseSchema: DashboardResponse }),
  });
}

export interface LatestSettlementDisplay {
  value: string;
  date: string;
  status: string;
}

/** User-facing latest-settlement copy. Missing or invalid source fields stay
 * unavailable instead of displaying an estimated or mis-currency amount. */
export function latestSettlementDisplay(
  position: Pick<
    DashboardResponse['positions'],
    'lastPayoutCents' | 'lastPayoutCurrency' | 'lastPayoutAt' | 'lastPayoutStatus'
  >
): LatestSettlementDisplay | null {
  if (
    position.lastPayoutCents == null ||
    !position.lastPayoutCurrency?.trim() ||
    !position.lastPayoutAt
  ) {
    return null;
  }
  const date = new Date(position.lastPayoutAt);
  if (Number.isNaN(date.getTime())) return null;
  const status =
    position.lastPayoutStatus === 'reconciled'
      ? 'Reconciled'
      : position.lastPayoutStatus === 'partial'
        ? 'Partial — review in Fee Center'
        : 'Unverified';
  return {
    value: formatFeeCenterCurrency(position.lastPayoutCents, position.lastPayoutCurrency),
    date: date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }),
    status,
  };
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
/** Same conventions as the daily brief: 48h claim horizon, 3h sync
 *  staleness (hourly cron), 30-day shipment clock. */
const CLAIM_RISK_WINDOW_MS = 48 * HOUR_MS;
const SYNC_STALE_MS = 3 * HOUR_MS;
const SHIPMENT_CLOCK_DAYS = 30;

export interface AttentionItem {
  key: string;
  severity: 'danger' | 'warn';
  title: string;
  detail: string;
  amountCents: number | null;
  href: string | null;
}

/** The attention ledger: everything that needs a human, danger before warn,
 *  then ranked by dollar amount (nulls last). Mirrors the daily brief's
 *  rules so morning message and home page agree. */
export function buildAttentionItems(
  data: DashboardResponse,
  now: Date,
  opts: { ordersEnabled: boolean }
): AttentionItem[] {
  const items: AttentionItem[] = [];

  for (const c of data.claims ?? []) {
    const title = `${c.kind === 'damage' ? 'Damage' : 'Short'} claim — ${c.vendor}${
      c.poExternalId ? ` #${c.poExternalId}` : ''
    }`;
    const state = claimDeadlineState(c.deadlineAt, now, CLAIM_RISK_WINDOW_MS);
    if (state == null) continue;
    items.push({
      key: `claim-${c.id}`,
      severity: state.kind === 'overdue' ? 'danger' : 'warn',
      title,
      detail: state.label === 'No deadline' ? 'No deadline recorded — file it now' : state.label,
      amountCents: c.amountCents,
      href: opts.ordersEnabled ? '/app/orders' : null,
    });
  }

  const run = data.sync?.lastRun;
  if (data.sync && !run) {
    items.push({
      key: 'sync-never',
      severity: 'danger',
      title: 'Amazon sync has never run',
      detail: 'No sync runs recorded — check the Worker cron',
      amountCents: null,
      href: '/app/amazon',
    });
  } else if (run) {
    const ageMs = now.getTime() - new Date(run.startedAt).getTime();
    if (run.status === 'error') {
      items.push({
        key: 'sync-error',
        severity: 'danger',
        title: 'Amazon sync failed',
        detail: run.error ?? 'No error message recorded',
        amountCents: null,
        href: '/app/amazon',
      });
    } else if (ageMs > SYNC_STALE_MS) {
      items.push({
        key: 'sync-stale',
        severity: 'warn',
        title: 'Amazon sync is stale',
        detail: `Last run ${Math.floor(ageMs / HOUR_MS)}h ago — hourly cron expected`,
        amountCents: null,
        href: '/app/amazon',
      });
    }
  }

  for (const s of data.shipments ?? []) {
    if (!s.shippedAt) continue;
    const days = Math.floor((now.getTime() - new Date(s.shippedAt).getTime()) / DAY_MS);
    if (days < SHIPMENT_CLOCK_DAYS) continue;
    items.push({
      key: `shipment-${s.id}`,
      severity: 'warn',
      title: `${s.shipmentId}${s.name ? ` (${s.name})` : ''}`,
      detail: `${days} days in transit — over the 30-day clock`,
      amountCents: null,
      href: '/app/shipments',
    });
  }

  const residualStatus = data.positions.residualStatus as string | null | undefined;
  const residualCents = data.positions.residualCents;
  if (
    residualStatus === 'ready' &&
    residualCents != null &&
    residualCents > UNIT_FLOW_ATTENTION_THRESHOLD_CENTS
  ) {
    items.push({
      key: 'unit-flow-residual',
      severity: 'warn',
      title: 'Unit flow has residual exposure',
      detail: 'Review the unit-flow ledger — residual units need attention',
      amountCents: residualCents,
      href: '/app/amazon',
    });
  }

  return items.sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === 'danger' ? -1 : 1;
    if (a.amountCents == null && b.amountCents == null) return 0;
    if (a.amountCents == null) return 1;
    if (b.amountCents == null) return -1;
    return b.amountCents - a.amountCents;
  });
}

const SMALL_COUNTS = [
  'Nothing',
  'One thing',
  'Two things',
  'Three things',
  'Four things',
  'Five things',
  'Six things',
  'Seven things',
  'Eight things',
  'Nine things',
];

/** The masthead sentence. Counts read as words while they stay humane. */
export function attentionHeadline(count: number): string {
  if (count === 0) return 'Nothing needs you.';
  const subject = count < SMALL_COUNTS.length ? SMALL_COUNTS[count]! : `${count} things`;
  return `${subject} need${count === 1 ? 's' : ''} you.`;
}

export type UnitFlowDisplay =
  | {
      kind: 'disabled';
      title: 'Unit flow';
      description: 'Connect the Amazon module to see unit-flow exposure.';
      phase: 'Module';
    }
  | {
      kind: 'backfill-incomplete';
      title: 'Unit flow';
      description: 'Amazon source backfill is incomplete; inventory variance is not yet valued.';
      phase: 'Backfill';
    }
  | {
      kind: 'missing-cost';
      title: 'Unit flow';
      description: 'Dollar exposure is unavailable until every nonzero inventory variance has a contributing cost.';
      phase: 'Incomplete';
    }
  | {
      kind: 'unknown';
      title: 'Unit flow';
      description: 'Unit-flow valuation is unavailable — refresh to verify source coverage.';
      phase: 'Unavailable';
    }
  | { kind: 'value'; residualCents: number };

/** Distinguish an unavailable Amazon module from an enabled ledger whose
 * nonzero residuals cannot yet be valued. */
export function unitFlowDisplay(data: DashboardResponse): UnitFlowDisplay {
  if (data.sync === null) {
    return {
      kind: 'disabled',
      title: 'Unit flow',
      description: 'Connect the Amazon module to see unit-flow exposure.',
      phase: 'Module',
    };
  }
  const residualStatus = data.positions.residualStatus as string | null | undefined;
  if (residualStatus === 'source_backfill') {
    return {
      kind: 'backfill-incomplete',
      title: 'Unit flow',
      description: 'Amazon source backfill is incomplete; inventory variance is not yet valued.',
      phase: 'Backfill',
    };
  }
  if (residualStatus === 'missing_cost') {
    return {
      kind: 'missing-cost',
      title: 'Unit flow',
      description:
        'Dollar exposure is unavailable until every nonzero inventory variance has a contributing cost.',
      phase: 'Incomplete',
    };
  }
  if (residualStatus !== 'ready' || data.positions.residualCents == null) {
    return {
      kind: 'unknown',
      title: 'Unit flow',
      description: 'Unit-flow valuation is unavailable — refresh to verify source coverage.',
      phase: 'Unavailable',
    };
  }
  return { kind: 'value', residualCents: data.positions.residualCents };
}
